import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState, type CSSProperties } from 'react';
import {
  Button,
  Card,
  Dialog,
  H2,
  H4,
  Input,
  Label,
  Paragraph,
  ScrollView,
  Separator,
  SizableText,
  XStack,
  YStack,
} from 'tamagui';
import {
  AlertTriangle,
  Check,
  FileDown,
  MoreHorizontal,
  Trash2,
} from '@tamagui/lucide-icons';
import {
  BASELINE_SERVICE_TYPES,
  type MaintenanceScheduleEntry,
  type VehicleRecalls,
} from '@cartrack/shared';
import {
  api,
  type ScheduledServiceDto,
  type ScheduledServiceState,
  type ServiceLogEntry,
} from '../lib/api';
import { friendlyErrorMessage } from '../lib/errors';
import { DeleteVehicleDialog } from '../components/DeleteVehicleDialog';
import { DeleteServiceLogDialog } from '../components/DeleteServiceLogDialog';
import {
  uploadReceipt,
  getReceiptSignedUrl,
  uploadVehiclePhoto,
  getVehiclePhotoSignedUrl,
} from '../lib/supabase';
import { Image } from 'tamagui';
import { useAuthStore } from '../lib/auth-store';
import {
  assembleSaleRecord,
  canExportSaleRecord,
  isPaywallEnabled,
  renderSaleRecordPdf,
} from '../lib/sale-export';
import { useMe } from '../lib/user-prefs';
import { sortSchedule, statusColor, statusLabel } from '../lib/schedule-view';
import { unitSuffix, useUnitSystem } from '../lib/user-prefs';
import { ACCENT_FOCUS_VISIBLE_STYLE } from '../lib/focus-ring';
import { useMenuKeyboard } from '../lib/use-menu-keyboard';
import { FocusRingButton } from '../components/FocusRingButton';
import { Skeleton, SkeletonText, SkeletonPage } from '../components/Skeleton';
import { Z } from '../lib/z-index';

// Minimum tap-target size (px) for touch controls (per-row Delete, the overflow
// trigger, and each overflow menu item).
const MIN_TAP_TARGET_PX = 44;

// Overflow ("More actions") menu geometry + focus-ring sizing.
const MENU_MIN_WIDTH_PX = 200;
const MENU_TOP_OFFSET_PX = MIN_TAP_TARGET_PX + 6;
const FOCUS_RING_WIDTH_PX = 2;
const FOCUS_RING_OFFSET_PX = 2;

// Icon component shape, derived from a lucide icon (avoids depending on
// @tamagui/helpers-icon resolving in this workspace).
type ActionIcon = typeof Trash2;

interface VehicleAction {
  label: string;
  icon: ActionIcon;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
}

// Full-viewport invisible layer so an outside click dismisses the overflow menu.
const FIXED_FILL: CSSProperties = { position: 'fixed', inset: 0 };

export function VehicleDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const unit = useUnitSystem();
  const suffix = unitSuffix(unit);

  const vehicleQuery = useQuery({
    queryKey: ['vehicle', id],
    queryFn: () => api.getVehicle(id),
    enabled: Boolean(id),
  });
  const scheduleQuery = useQuery({
    queryKey: ['schedule', id],
    queryFn: () => api.getSchedule(id),
    enabled: Boolean(id),
  });
  const logsQuery = useQuery({
    queryKey: ['logs', id],
    queryFn: () => api.listLogs(id),
    enabled: Boolean(id),
  });
  const recallsQuery = useQuery({
    queryKey: ['recalls', id],
    queryFn: () => api.getRecalls(id),
    enabled: Boolean(id),
    staleTime: 24 * 60 * 60 * 1000, // recalls change rarely; cache for a day
    retry: false,
  });
  const scheduledQuery = useQuery({
    queryKey: ['scheduled-services', id],
    queryFn: () => api.listScheduledServices(id),
    enabled: Boolean(id),
  });

  const scheduledUpdateMut = useMutation({
    mutationFn: ({ ssId, state }: { ssId: string; state: ScheduledServiceState }) =>
      api.updateScheduledService(id, ssId, { state }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scheduled-services', id] }),
  });

  const [logDialog, setLogDialog] = useState<{ open: boolean; prefillType?: string }>({
    open: false,
  });
  const [mileageUpdate, setMileageUpdate] = useState('');
  const [mileageError, setMileageError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLog, setDeleteLog] = useState<ServiceLogEntry | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const { me } = useMe();
  const exportGate = canExportSaleRecord({
    plan: me?.plan ?? 'free',
    paywallEnabled: isPaywallEnabled(),
  });

  const deleteVehicleMut = useMutation({
    mutationFn: () => api.deleteVehicle(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicles'] });
      navigate('/vehicles');
    },
  });

  async function exportSaleRecord(): Promise<void> {
    if (!exportGate.enabled || !vehicleQuery.data || !me) return;
    setExporting(true);
    setExportError(null);
    try {
      const record = assembleSaleRecord({
        vehicle: vehicleQuery.data,
        logs: logsQuery.data ?? [],
        schedule: scheduleQuery.data ?? [],
        recalls: recallsQuery.data?.recalls ?? [],
        me,
        now: new Date(),
      });
      await renderSaleRecordPdf(record, { unitSystem: unit });
    } catch (err) {
      setExportError(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  }

  const mileageMut = useMutation({
    mutationFn: (mileage: number) => api.updateMileage(id, mileage),
    onSuccess: () => {
      setMileageError(null);
      setMileageUpdate('');
      qc.invalidateQueries({ queryKey: ['vehicle', id] });
      qc.invalidateQueries({ queryKey: ['schedule', id] });
      qc.invalidateQueries({ queryKey: ['vehicles'] });
    },
    onError: (err) => {
      setMileageError(friendlyErrorMessage(err));
    },
  });

  const deleteLogMut = useMutation({
    mutationFn: (logId: string) => api.deleteLog(id, logId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['schedule', id] });
      qc.invalidateQueries({ queryKey: ['logs', id] });
      qc.invalidateQueries({ queryKey: ['vehicles'] });
    },
  });

  if (vehicleQuery.isLoading) {
    return <SkeletonPage headingWidth={220} paddingBottom="$6" />;
  }
  if (vehicleQuery.isError || !vehicleQuery.data) {
    return (
      <YStack gap="$3">
        <Paragraph color="$red10">Vehicle not found.</Paragraph>
        <Button onPress={() => navigate('/vehicles')}>Back to vehicles</Button>
      </YStack>
    );
  }

  const v = vehicleQuery.data;
  const schedule = scheduleQuery.data ?? [];
  const sortedSchedule = sortSchedule(schedule);
  const logs = logsQuery.data ?? [];

  return (
    <YStack gap="$4" paddingBottom="$6">
      <YStack gap="$3" $gtSm={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <YStack>
          <H2>
            {v.year} {v.make} {v.model}
          </H2>
          <Paragraph color="$color10">
            {v.trim ?? '—'} · {v.engine ?? '—'} · {v.vin ?? 'no VIN'}
          </Paragraph>
        </YStack>
        <XStack gap="$2" alignItems="center" flexWrap="wrap">
          <FocusRingButton
            theme="blue"
            flexGrow={1}
            $gtSm={{ flexGrow: 0 }}
            onPress={() => setLogDialog({ open: true })}
          >
            Log service
          </FocusRingButton>
          {/* Neutral (default-fill) secondary action — kept top-level so Log
              service remains the sole Signal-Blue primary in the header. */}
          <FocusRingButton
            flexGrow={1}
            $gtSm={{ flexGrow: 0 }}
            onPress={() => navigate(`/vehicles/${id}/chat`)}
          >
            Diagnostic chat
          </FocusRingButton>
          <FocusRingButton
            flexGrow={1}
            $gtSm={{ flexGrow: 0 }}
            onPress={() => navigate('/vehicles')}
          >
            Back
          </FocusRingButton>
          <VehicleActionsMenu
            actions={[
              {
                label: 'Export sale record',
                icon: FileDown,
                disabled: !exportGate.enabled || exporting,
                onSelect: () => {
                  void exportSaleRecord();
                },
              },
              {
                label: 'Delete',
                icon: Trash2,
                danger: true,
                onSelect: () => setShowDeleteConfirm(true),
              },
            ]}
          />
          {isPaywallEnabled() ? <PremiumChip /> : null}
        </XStack>
      </YStack>
      {exportError ? (
        <Paragraph color="$red10" size="$2">
          Export failed: {exportError}
        </Paragraph>
      ) : null}
      {!exportGate.enabled && exportGate.reason ? (
        <Paragraph color="$color10" size="$1">
          {exportGate.reason}
        </Paragraph>
      ) : null}

      <VehiclePhoto vehicleId={id} photoUrl={v.photoUrl} />

      <DeleteVehicleDialog
        vehicle={showDeleteConfirm ? v : null}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={() => deleteVehicleMut.mutate()}
        isPending={deleteVehicleMut.isPending}
      />

      <DeleteServiceLogDialog
        log={deleteLog}
        onCancel={() => setDeleteLog(null)}
        onConfirm={() => {
          if (!deleteLog) return;
          deleteLogMut.mutate(deleteLog.id);
          setDeleteLog(null);
        }}
        isPending={deleteLogMut.isPending}
      />

      {/* Recalls */}
      <RecallsSection
        data={recallsQuery.data}
        loading={recallsQuery.isLoading}
        error={recallsQuery.isError}
      />

      {/* Mileage */}
      <Card padding="$4" elevate>
        <YStack gap="$3">
          <H4>Odometer</H4>
          <Paragraph size="$3">
            Current: <strong>{v.currentMileage.toLocaleString()} {suffix}</strong>
          </Paragraph>
          <Paragraph size="$2" color="$color10">
            Updated {new Date(v.mileageUpdatedAt).toLocaleDateString()}
          </Paragraph>
          <XStack gap="$2" alignItems="flex-end">
            <YStack flex={1} gap="$1">
              <Label>New odometer reading</Label>
              <Input
                value={mileageUpdate}
                onChangeText={setMileageUpdate}
                keyboardType="number-pad"
                placeholder={`≥ ${v.currentMileage} ${suffix}`}
              />
            </YStack>
            <Button
              theme="blue"
              onPress={() => mileageMut.mutate(Number(mileageUpdate))}
              disabled={!mileageUpdate || mileageMut.isPending}
            >
              Update
            </Button>
          </XStack>
          {mileageError ? <Paragraph color="$red10">{mileageError}</Paragraph> : null}
        </YStack>
      </Card>

      {/* Maintenance schedule — computed status rows + manufacturer checkpoints,
          merged into a single card. */}
      <Card padding="$4" elevate>
        <YStack gap="$3">
          <H4>Maintenance schedule</H4>
          {scheduleQuery.isLoading ? (
            <SkeletonText />
          ) : scheduleQuery.isError ? (
            <Paragraph color="$red10">Failed to load schedule.</Paragraph>
          ) : (
            <YStack gap="$2">
              {sortedSchedule.map((entry) => (
                <ScheduleRow
                  key={entry.serviceType}
                  entry={entry}
                  suffix={suffix}
                  onLog={() => setLogDialog({ open: true, prefillType: entry.serviceType })}
                />
              ))}
            </YStack>
          )}

          <Separator />

          <YStack gap="$3">
            <Paragraph fontWeight="600">Manufacturer checkpoints</Paragraph>
            <Paragraph size="$2" color="$color10">
              Every checkpoint from the manufacturer's recommended schedule. Mark each as
              serviced, or "no data" if you don't know its history.
            </Paragraph>
            {scheduledQuery.isLoading ? (
              <SkeletonText />
            ) : scheduledQuery.isError ? (
              <Paragraph color="$red10">
                Failed to load manufacturer schedule. This can take 30s on a new vehicle while the
                Edmunds scraper runs; try refreshing.
              </Paragraph>
            ) : (scheduledQuery.data ?? []).length === 0 ? (
              <Paragraph color="$color10">
                No manufacturer schedule yet. This populates a few seconds after adding a
                vehicle — refresh to check again.
              </Paragraph>
            ) : (
              <YStack gap="$1">
                {(scheduledQuery.data ?? []).map((ss) => (
                  <ScheduledServiceRow
                    key={ss.id}
                    ss={ss}
                    suffix={suffix}
                    currentMileage={v.currentMileage}
                    onState={(state) =>
                      scheduledUpdateMut.mutate({ ssId: ss.id, state })
                    }
                    pending={
                      scheduledUpdateMut.isPending && scheduledUpdateMut.variables?.ssId === ss.id
                    }
                  />
                ))}
              </YStack>
            )}
          </YStack>
        </YStack>
      </Card>

      {/* Service history */}
      <Card padding="$4" elevate>
        <YStack gap="$3">
          <H4>Service history</H4>
          {logsQuery.isLoading ? (
            <SkeletonText />
          ) : logs.length === 0 ? (
            <Paragraph color="$color10">
              No service history yet. Log your first service above.
            </Paragraph>
          ) : (
            <YStack gap="$2">
              {logs.map((log) => (
                <LogRow
                  key={log.id}
                  log={log}
                  suffix={suffix}
                  onDelete={() => setDeleteLog(log)}
                  deleting={deleteLogMut.isPending}
                />
              ))}
            </YStack>
          )}
        </YStack>
      </Card>

      <LogServiceDialog
        open={logDialog.open}
        onClose={() => setLogDialog({ open: false })}
        vehicleId={id}
        defaultMileage={v.currentMileage}
        prefillType={logDialog.prefillType}
        onSuccess={() => {
          setLogDialog({ open: false });
          qc.invalidateQueries({ queryKey: ['schedule', id] });
          qc.invalidateQueries({ queryKey: ['logs', id] });
          qc.invalidateQueries({ queryKey: ['vehicles'] });
        }}
      />
    </YStack>
  );
}

/**
 * Overflow ("More actions") menu for the vehicle header. Reuses the AppShell
 * AccountMenu / MenuItem pattern: a `tag="button"` trigger with
 * `aria-haspopup="menu"` + `aria-expanded`, a `role="menu"` surface of
 * `role="menuitem"` items, outside-click dismissal via a full-viewport backdrop,
 * and `danger` styling for the destructive item. Trigger + items are >=44x44px
 * tap targets and keyboard-operable (a real <button> activates on Enter/Space).
 */
function VehicleActionsMenu({ actions }: { actions: VehicleAction[] }) {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  useMenuKeyboard(open, close, 'More actions');

  return (
    <YStack position="relative">
      <XStack
        tag="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More actions"
        alignItems="center"
        justifyContent="center"
        width={MIN_TAP_TARGET_PX}
        height={MIN_TAP_TARGET_PX}
        borderRadius="$3"
        backgroundColor="transparent"
        borderWidth={0}
        cursor="pointer"
        hoverStyle={{ backgroundColor: '$backgroundHover' }}
        pressStyle={{ backgroundColor: '$backgroundPress' }}
        // Explicit, state-driven focus ring so a visible outline is present under
        // programmatic focus (getComputedStyle), independent of :focus-visible.
        outlineColor="$accent"
        outlineStyle={focused ? 'solid' : 'none'}
        outlineWidth={focused ? FOCUS_RING_WIDTH_PX : 0}
        outlineOffset={FOCUS_RING_OFFSET_PX}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onPress={() => setOpen((v) => !v)}
      >
        <MoreHorizontal size={20} color="$color10" />
      </XStack>

      {open ? (
        <>
          <ActionsBackdrop onPress={close} />
          <YStack
            position="absolute"
            top={MENU_TOP_OFFSET_PX}
            right={0}
            minWidth={MENU_MIN_WIDTH_PX}
            zIndex={Z.dropdown}
            backgroundColor="$color3"
            borderWidth={1}
            borderColor="$borderColor"
            borderRadius="$4"
            paddingVertical="$2"
            elevation="$4"
            role="menu"
          >
            {actions.map((action) => (
              <VehicleActionItem
                key={action.label}
                action={action}
                onSelect={() => {
                  close();
                  action.onSelect();
                }}
              />
            ))}
          </YStack>
        </>
      ) : null}
    </YStack>
  );
}

function VehicleActionItem({
  action,
  onSelect,
}: {
  action: VehicleAction;
  onSelect: () => void;
}) {
  const Icon = action.icon;
  return (
    <XStack
      tag="button"
      role="menuitem"
      alignItems="center"
      gap="$3"
      width="100%"
      minHeight={MIN_TAP_TARGET_PX}
      paddingHorizontal="$3"
      paddingVertical="$2"
      backgroundColor="transparent"
      borderWidth={0}
      cursor="pointer"
      opacity={action.disabled ? 0.5 : 1}
      // aria-disabled (not the native `disabled` attribute) keeps a gated action
      // (e.g. paywalled export) focusable for keyboard/roving focus + focus ring,
      // while onPress stays inert.
      aria-disabled={action.disabled}
      hoverStyle={{ backgroundColor: '$backgroundHover' }}
      pressStyle={{ backgroundColor: '$backgroundPress' }}
      focusVisibleStyle={ACCENT_FOCUS_VISIBLE_STYLE}
      onPress={action.disabled ? undefined : onSelect}
    >
      <Icon size={18} color={action.danger ? '$danger' : '$color10'} />
      <SizableText size="$3" color={action.danger ? '$danger' : '$color11'}>
        {action.label}
      </SizableText>
    </XStack>
  );
}

function ActionsBackdrop({ onPress }: { onPress: () => void }) {
  return <YStack style={FIXED_FILL} zIndex={Z.backdrop} onPress={onPress} />;
}

/**
 * Premium indicator: the design system's neutral chip (hairline `$color5`
 * background, secondary-ink `$color10` text) — off the service-status ramp, in
 * both themes. Calm, informational label; never a salesy nudge.
 */
function PremiumChip() {
  return (
    <Paragraph
      size="$1"
      paddingHorizontal="$2"
      paddingVertical="$1"
      borderRadius="$2"
      backgroundColor="$color5"
      color="$color10"
    >
      Premium
    </Paragraph>
  );
}

function RecallsSection({
  data,
  loading,
  error,
}: {
  data: VehicleRecalls | undefined;
  loading: boolean;
  error: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  if (loading) {
    return (
      <Card padding="$4" elevate>
        <SkeletonText />
      </Card>
    );
  }
  if (error || !data || !Array.isArray(data.recalls)) {
    return (
      <Card padding="$4" elevate>
        <Paragraph color="$orange10">
          Couldn't fetch recalls from NHTSA. Retry in a minute.
        </Paragraph>
      </Card>
    );
  }

  const { recalls, status, lastCheckedAt } = data;

  if (status === 'incomplete') {
    return (
      <Card padding="$3" elevate>
        <Paragraph color="$color10" size="$2">
          Add the make, model, and year to check for recalls.
        </Paragraph>
      </Card>
    );
  }

  if (status === 'checking') {
    return (
      <Card padding="$3" elevate>
        <Paragraph color="$color10" size="$2">
          Checking for recalls…
        </Paragraph>
      </Card>
    );
  }

  const lastChecked = lastCheckedAt
    ? `Last checked ${new Date(lastCheckedAt).toLocaleDateString()}`
    : null;

  if (recalls.length === 0) {
    return (
      <Card padding="$3" elevate backgroundColor="$green2">
        <YStack gap="$1">
          <XStack gap="$2" alignItems="center">
            <Check size={18} color="$green10" />
            <Paragraph color="$green10" size="$2">
              No open recalls for this vehicle.
            </Paragraph>
          </XStack>
          {lastChecked ? (
            <Paragraph color="$color10" size="$1">
              {lastChecked}
            </Paragraph>
          ) : null}
        </YStack>
      </Card>
    );
  }
  return (
    <Card padding="$4" elevate backgroundColor="$red2" borderColor="$red8" borderWidth={1}>
      <YStack gap="$3">
        <XStack alignItems="center" justifyContent="space-between">
          <XStack gap="$2" alignItems="center">
            <AlertTriangle size={20} color="$red10" />
            <Paragraph color="$red10" fontWeight="700" size="$5">
              {recalls.length} open recall{recalls.length === 1 ? '' : 's'}
            </Paragraph>
          </XStack>
          <Button
            size="$2"
            theme="red"
            minHeight={MIN_TAP_TARGET_PX}
            minWidth={MIN_TAP_TARGET_PX}
            onPress={() => setExpanded((x) => !x)}
          >
            {expanded ? 'Hide details' : 'Show details'}
          </Button>
        </XStack>
        {lastChecked ? (
          <Paragraph color="$color10" size="$1">
            {lastChecked}
          </Paragraph>
        ) : null}
        {expanded ? (
          <YStack gap="$3">
            {recalls.map((r, i) => (
              <YStack
                key={r.campaignNumber || i}
                gap="$1"
                padding="$3"
                borderRadius="$3"
                backgroundColor="$red3"
              >
                <XStack gap="$2" alignItems="baseline" flexWrap="wrap">
                  <Paragraph fontWeight="700" color="$red11">
                    {r.component || 'Unknown component'}
                  </Paragraph>
                  <Paragraph size="$1" color="$color10">
                    Campaign {r.campaignNumber || '—'}
                  </Paragraph>
                  {r.reportDate ? (
                    <Paragraph size="$1" color="$color10">
                      · {new Date(r.reportDate).toLocaleDateString()}
                    </Paragraph>
                  ) : null}
                  {r.source === 'TC' ? (
                    <Paragraph size="$1" color="$color10">
                      Transport Canada
                    </Paragraph>
                  ) : null}
                </XStack>
                {r.summary ? (
                  <Paragraph size="$2">
                    <strong>Summary:</strong> {r.summary}
                  </Paragraph>
                ) : null}
                {r.consequence ? (
                  <Paragraph size="$2" color="$red11">
                    <strong>Consequence:</strong> {r.consequence}
                  </Paragraph>
                ) : null}
                {r.remedy ? (
                  <Paragraph size="$2">
                    <strong>Remedy:</strong> {r.remedy}
                  </Paragraph>
                ) : null}
              </YStack>
            ))}
          </YStack>
        ) : null}
      </YStack>
    </Card>
  );
}

function ScheduleRow({
  entry,
  suffix,
  onLog,
}: {
  entry: MaintenanceScheduleEntry;
  suffix: 'km' | 'mi';
  onLog: () => void;
}) {
  const last =
    entry.lastMileage !== null
      ? `${entry.lastMileage.toLocaleString()} ${suffix}`
      : 'never';
  const next =
    entry.nextMileage !== null
      ? `${entry.nextMileage.toLocaleString()} ${suffix}`
      : entry.nextDate
        ? new Date(entry.nextDate).toLocaleDateString()
        : '—';
  const urgency =
    entry.milesUntilDue !== null
      ? `${entry.milesUntilDue >= 0 ? '' : ''}${entry.milesUntilDue.toLocaleString()} ${suffix}`
      : entry.daysUntilDue !== null
        ? `${entry.daysUntilDue} days`
        : '—';

  return (
    <XStack
      alignItems="center"
      justifyContent="space-between"
      paddingVertical="$2"
      gap="$2"
    >
      <YStack flex={1} minWidth={160} gap="$1">
        <Paragraph fontWeight="600">{entry.serviceType}</Paragraph>
        <XStack gap="$2" alignItems="center">
          <Paragraph
            size="$1"
            paddingHorizontal="$2"
            paddingVertical={2}
            borderRadius="$2"
            backgroundColor={
              entry.status === 'OVERDUE'
                ? '$red4'
                : entry.status === 'DUE_SOON'
                  ? '$orange4'
                  : entry.status === 'UPCOMING'
                    ? '$blue2'
                    : '$green4'
            }
            color={statusColor(entry.status)}
            fontWeight="700"
          >
            {statusLabel(entry.status)}
          </Paragraph>
          <Paragraph size="$1" color="$color10">
            {urgency}
          </Paragraph>
        </XStack>
      </YStack>
      <YStack alignItems="flex-end" minWidth={120}>
        <Paragraph size="$1" color="$color10">
          Last: {last}
        </Paragraph>
        <Paragraph size="$1" color="$color10">
          Next: {next}
        </Paragraph>
      </YStack>
      <Button
        size="$2"
        minHeight={MIN_TAP_TARGET_PX}
        minWidth={MIN_TAP_TARGET_PX}
        onPress={onLog}
      >
        Log
      </Button>
    </XStack>
  );
}

function LogRow({
  log,
  suffix,
  onDelete,
  deleting,
}: {
  log: ServiceLogEntry;
  suffix: 'km' | 'mi';
  onDelete: () => void;
  deleting: boolean;
}) {
  return (
    <XStack
      alignItems="center"
      justifyContent="space-between"
      paddingVertical="$2"
      gap="$2"
    >
      <YStack flex={1} gap="$1">
        <XStack gap="$3" alignItems="center" flexWrap="wrap">
          <Paragraph fontWeight="600">{log.serviceType}</Paragraph>
          <Paragraph size="$1" color="$color10">
            {new Date(log.date).toLocaleDateString()}
          </Paragraph>
          <Paragraph size="$1" color="$color10">
            {log.mileage.toLocaleString()} {suffix}
          </Paragraph>
          {log.cost !== null ? (
            <Paragraph size="$1" color="$color10">
              ${Number(log.cost).toFixed(2)}
            </Paragraph>
          ) : null}
          {log.mechanic ? (
            <Paragraph size="$1" color="$color10">
              · {log.mechanic}
            </Paragraph>
          ) : null}
        </XStack>
        {log.notes ? (
          <Paragraph size="$1" color="$color9">
            {log.notes}
          </Paragraph>
        ) : null}
        {log.receiptUrl ? <ReceiptLink path={log.receiptUrl} /> : null}
      </YStack>
      <Button
        theme="red"
        minHeight={MIN_TAP_TARGET_PX}
        minWidth={MIN_TAP_TARGET_PX}
        onPress={onDelete}
        disabled={deleting}
      >
        Delete
      </Button>
    </XStack>
  );
}

function ReceiptLink({ path }: { path: string }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function open(): Promise<void> {
    setLoading(true);
    setErr(null);
    try {
      const url = await getReceiptSignedUrl(path);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }
  return (
    <XStack gap="$2" alignItems="center">
      <Button
        size="$1"
        minHeight={MIN_TAP_TARGET_PX}
        minWidth={MIN_TAP_TARGET_PX}
        onPress={open}
        disabled={loading}
      >
        {loading ? 'Loading…' : 'View receipt'}
      </Button>
      {err ? (
        <Paragraph size="$1" color="$red10">
          {err}
        </Paragraph>
      ) : null}
    </XStack>
  );
}

const PHOTO_MAX_HEIGHT = 280;

/**
 * Vehicle photo card: shows the current photo via a short-lived signed URL and
 * lets the owner add / replace / remove it. Upload mirrors the receipt flow
 * (private owner-scoped Storage), then the returned path is PATCHed onto the
 * vehicle as `photoUrl`. Responsive: full-width image up to a max height, with
 * a >=44px tap-target picker button.
 */
function VehiclePhoto({
  vehicleId,
  photoUrl,
}: {
  vehicleId: string;
  photoUrl: string | null;
}): JSX.Element {
  const qc = useQueryClient();
  const userId = useAuthStore((s) => s.session?.user.id);
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signedQuery = useQuery({
    queryKey: ['vehicle-photo-url', photoUrl],
    queryFn: () => getVehiclePhotoSignedUrl(photoUrl as string),
    enabled: Boolean(photoUrl),
    staleTime: 50 * 60 * 1000, // refresh before the 1h signed-URL expiry
  });

  async function persist(nextPath: string): Promise<void> {
    await api.updateVehicle(vehicleId, { photoUrl: nextPath });
    qc.invalidateQueries({ queryKey: ['vehicle', vehicleId] });
    qc.invalidateQueries({ queryKey: ['vehicles'] });
  }

  async function handleFile(file: File): Promise<void> {
    if (!userId) {
      setError('Not signed in');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const path = await uploadVehiclePhoto(userId, vehicleId, file);
      await persist(path);
    } catch (err) {
      setError(`Upload failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      // Empty string clears Vehicle.photoUrl to null server-side.
      await persist('');
    } catch (err) {
      setError(`Remove failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card bordered padding="$3">
      <YStack gap="$3">
        <H4>Photo</H4>
        {photoUrl ? (
          signedQuery.isLoading ? (
            <Skeleton height={PHOTO_MAX_HEIGHT} borderRadius="$3" />
          ) : signedQuery.isError ? (
            <Paragraph size="$2" color="$red10">
              Could not load photo.
            </Paragraph>
          ) : (
            <Image
              source={{ uri: signedQuery.data }}
              width="100%"
              height={PHOTO_MAX_HEIGHT}
              resizeMode="cover"
              borderRadius="$3"
              accessibilityLabel="Vehicle photo"
            />
          )
        ) : (
          <Paragraph size="$2" color="$color10">
            No photo yet.
          </Paragraph>
        )}

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            e.target.value = '';
          }}
        />
        <XStack gap="$2" flexWrap="wrap">
          <Button
            theme="blue"
            minHeight={44}
            flexGrow={1}
            $gtSm={{ flexGrow: 0 }}
            disabled={busy}
            onPress={() => fileRef.current?.click()}
          >
            {busy ? 'Saving…' : photoUrl ? 'Replace photo' : 'Add photo'}
          </Button>
          {photoUrl ? (
            <Button
              theme="red"
              minHeight={44}
              flexGrow={1}
              $gtSm={{ flexGrow: 0 }}
              disabled={busy}
              onPress={() => void handleRemove()}
            >
              Remove
            </Button>
          ) : null}
        </XStack>
        {error ? (
          <Paragraph size="$2" color="$red10">
            {error}
          </Paragraph>
        ) : null}
      </YStack>
    </Card>
  );
}

function LogServiceDialog({
  open,
  onClose,
  vehicleId,
  defaultMileage,
  prefillType,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  vehicleId: string;
  defaultMileage: number;
  prefillType?: string;
  onSuccess: () => void;
}) {
  const [serviceType, setServiceType] = useState<string>(prefillType ?? 'Oil Change');
  const [customType, setCustomType] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [mileage, setMileage] = useState(String(defaultMileage));
  const [cost, setCost] = useState('');
  const [mechanic, setMechanic] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [receiptPath, setReceiptPath] = useState<string | null>(null);
  const [receiptName, setReceiptName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const userId = useAuthStore((s) => s.session?.user.id);

  // Reset every field each time the dialog OPENS (closed → open), so a prior
  // entry never lingers after Save/Cancel and the prefill is re-applied fresh.
  const prevOpen = useRef(false);
  if (open && !prevOpen.current) {
    setServiceType(prefillType ?? 'Oil Change');
    setCustomType('');
    setDate(new Date().toISOString().slice(0, 10));
    setMileage(String(defaultMileage));
    setCost('');
    setMechanic('');
    setNotes('');
    setReceiptPath(null);
    setReceiptName(null);
    setError(null);
  }
  prevOpen.current = open;

  async function handleFile(file: File): Promise<void> {
    if (!userId) {
      setError('Not signed in');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const path = await uploadReceipt(userId, vehicleId, file);
      setReceiptPath(path);
      setReceiptName(file.name);
    } catch (err) {
      setError(`Upload failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setUploading(false);
    }
  }

  const mut = useMutation({
    mutationFn: () => {
      const resolved = serviceType === '__other' ? customType.trim() : serviceType;
      if (!resolved) throw new Error('Service type is required');
      return api.createLog(vehicleId, {
        date: new Date(date).toISOString(),
        mileage: Number(mileage),
        serviceType: resolved,
        cost: cost ? Number(cost) : undefined,
        mechanic: mechanic || undefined,
        notes: notes || undefined,
        receiptUrl: receiptPath ?? undefined,
      });
    },
    onSuccess: () => {
      setError(null);
      onSuccess();
    },
    onError: (err) => {
      setError(friendlyErrorMessage(err));
    },
  });

  return (
    <Dialog modal open={open} onOpenChange={(o) => (o ? undefined : onClose())}>
      <Dialog.Portal>
        <Dialog.Overlay key="overlay" opacity={0.5} />
        <Dialog.Content
          key="content"
          elevate
          padding="$4"
          width={440}
          maxWidth="90vw"
          maxHeight="90vh"
          gap="$3"
          // Keep the dialog open on outside clicks so an accidental click never
          // discards the form; Cancel/Save (and Esc) still close it.
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <Dialog.Title>Log service</Dialog.Title>

          {/* Scrollable body so Cancel/Save stay reachable at any zoom / short viewport */}
          <ScrollView flexShrink={1} minHeight={0}>
            <YStack gap="$3">
              <Label>Service type</Label>
          <select
            className="native-control"
            value={serviceType}
            onChange={(e) => setServiceType(e.target.value)}
          >
            {BASELINE_SERVICE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
            <option value="__other">Other…</option>
          </select>
          {serviceType === '__other' ? (
            <Input
              placeholder="Describe the service"
              value={customType}
              onChangeText={setCustomType}
            />
          ) : null}

          <YStack gap="$2" $gtSm={{ flexDirection: 'row' }}>
            <YStack flex={1} gap="$1">
              <Label>Date</Label>
              <input
                className="native-control"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </YStack>
            <YStack flex={1} gap="$1">
              <Label>Odometer</Label>
              <Input value={mileage} onChangeText={setMileage} keyboardType="number-pad" />
            </YStack>
          </YStack>

          <Separator />

          <YStack gap="$2" $gtSm={{ flexDirection: 'row' }}>
            <YStack flex={1} gap="$1">
              <Label>Cost (optional)</Label>
              <Input value={cost} onChangeText={setCost} placeholder="59.99" />
            </YStack>
            <YStack flex={1} gap="$1">
              <Label>Mechanic / shop</Label>
              <Input value={mechanic} onChangeText={setMechanic} placeholder="Jiffy Lube" />
            </YStack>
          </YStack>

          <Label>Notes (optional)</Label>
          <Input value={notes} onChangeText={setNotes} placeholder="Anything to remember" />

          <Label>Receipt (optional, image or PDF)</Label>
          <input
            className="native-control native-control--file"
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            disabled={uploading || mut.isPending}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
            }}
          />
          {uploading ? (
            <Paragraph size="$2" color="$color10">
              Uploading…
            </Paragraph>
          ) : receiptPath ? (
            <XStack gap="$2" alignItems="center">
              <Check size={16} color="$green10" />
              <Paragraph size="$2" color="$green10">
                Uploaded: {receiptName}
              </Paragraph>
            </XStack>
          ) : null}

              {error ? <Paragraph color="$red10">{error}</Paragraph> : null}
            </YStack>
          </ScrollView>

          <XStack gap="$2" justifyContent="flex-end">
            <Button onPress={onClose} disabled={mut.isPending}>
              Cancel
            </Button>
            <Button theme="blue" onPress={() => mut.mutate()} disabled={mut.isPending}>
              {mut.isPending ? 'Saving…' : 'Save log'}
            </Button>
          </XStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}

function ScheduledServiceRow({
  ss,
  suffix,
  currentMileage,
  onState,
  pending,
}: {
  ss: ScheduledServiceDto;
  suffix: 'km' | 'mi';
  currentMileage: number;
  onState: (state: ScheduledServiceState) => void;
  pending: boolean;
}) {
  const isPast = ss.dueMileage <= currentMileage;
  const stateColor: Record<ScheduledServiceState, string> = {
    PENDING: isPast ? '$orange10' : '$color10',
    SERVICED: '$green10',
    NO_DATA: '$color8',
  };
  const stateLabel: Record<ScheduledServiceState, string> = {
    PENDING: isPast ? 'Missed / pending' : 'Upcoming',
    SERVICED: 'Serviced',
    NO_DATA: 'No data',
  };

  return (
    <XStack
      alignItems="center"
      gap="$2"
      paddingVertical="$2"
      paddingHorizontal="$2"
      borderRadius="$2"
      backgroundColor={isPast ? '$color2' : 'transparent'}
      flexWrap="wrap"
    >
      <YStack flex={1} minWidth={220} gap="$1">
        <XStack gap="$2" alignItems="baseline" flexWrap="wrap">
          <Paragraph fontWeight="600">{ss.serviceType}</Paragraph>
          <Paragraph size="$2" color="$color10">
            @ {ss.dueMileage.toLocaleString()} {suffix}
            {ss.dueMonths ? ` · ${ss.dueMonths} mo` : ''}
          </Paragraph>
        </XStack>
        <Paragraph size="$1" color={stateColor[ss.state]}>
          {stateLabel[ss.state]}
          {ss.state === 'SERVICED' && ss.servicedAt
            ? ` · ${new Date(ss.servicedAt).toLocaleDateString()}`
            : ''}
        </Paragraph>
      </YStack>
      <XStack gap="$1">
        <Button
          size="$2"
          minHeight={MIN_TAP_TARGET_PX}
          minWidth={MIN_TAP_TARGET_PX}
          theme={ss.state === 'SERVICED' ? 'green' : undefined}
          disabled={pending}
          onPress={() => onState(ss.state === 'SERVICED' ? 'PENDING' : 'SERVICED')}
        >
          {ss.state === 'SERVICED' ? 'Unmark' : 'Serviced'}
        </Button>
        <Button
          size="$2"
          minHeight={MIN_TAP_TARGET_PX}
          minWidth={MIN_TAP_TARGET_PX}
          theme={ss.state === 'NO_DATA' ? 'gray' : undefined}
          disabled={pending}
          onPress={() => onState(ss.state === 'NO_DATA' ? 'PENDING' : 'NO_DATA')}
        >
          {ss.state === 'NO_DATA' ? 'Have data' : 'No data'}
        </Button>
      </XStack>
    </XStack>
  );
}
